import { ITypedBaseResponse } from "@nkr/models/base-response";
import { IStrongBoxOpoFilter, IStrongBoxOpoPhysicalInventoryContract, IStrongBoxOpoPhysicalInventoryListing } from "@nkr/models/strong-box";
import { AuthActionCreators } from "@nkr/store/authorization";
import { useAppHookContext } from "@nkr/utils/rest-api";
import { useEffect, useRef, useState } from "react";

interface IRetVal {
    list: IStrongBoxOpoPhysicalInventoryListing[],
    loading: boolean,
    error: string,
    record: (opoId: number, count: number, initials: string) => void,
    update: (id: number, count: number) => void
}

function useStrongBoxOpoPhysicalInventory(filter?: IStrongBoxOpoFilter, onRecorded?: (contract: IStrongBoxOpoPhysicalInventoryContract) => void): IRetVal {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<IStrongBoxOpoPhysicalInventoryListing[]>([]);

    const listRef = useRef<IStrongBoxOpoPhysicalInventoryListing[]>([]);

    useEffect(() => {
        setError('');

        const fetchData = async () => {
            setLoading(true);

            let config = {
                url: `${__API__}/strongbox/opo/physical-inventory/search`,
                data: filter
            };

            try {
                const response: ITypedBaseResponse<IStrongBoxOpoPhysicalInventoryListing[]> = await dispatch(AuthActionCreators.apiPost(config));

                if (response && response.data.results && response.data.success) {
                    setList(response.data.results);

                    listRef.current = response.data.results;
                } else {
                    setError('Unable to load data');
                    setList([]);

                    listRef.current = [];
                }

            } catch (err) {
                setList([]);
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        if (filter) fetchData();
    }, [filter]);

    const record = (opoId: number, count: number, initials: string) => {
        const postData = async () => {
            setLoading(true);

            let config = {
                url: `${__API__}/strongbox/opo/physical-inventory`,
                data: {
                    opoId,
                    count,
                    initials
                }
            };

            try {
                const response: ITypedBaseResponse<IStrongBoxOpoPhysicalInventoryListing> = await dispatch(AuthActionCreators.apiPost(config));

                if (response && response.data.results && response.data.success) {
                    const updatedList = [...(listRef.current || []), response.data.results];

                    setList(updatedList);
                    listRef.current = updatedList;
                    onRecorded?.(response.data.results);
                } else {
                    setError('Unable to record inventory');
                }
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        postData();
    };

    const update = (id: number, count: number) => {
        const putData = async () => {
            setLoading(true);

            let config = {
                url: `${__API__}/strongbox/opo/physical-inventory`,
                data: {
                    id,
                    count
                }
            };

            try {
                const response: ITypedBaseResponse<IStrongBoxOpoPhysicalInventoryListing> = await dispatch(AuthActionCreators.apiPut(config));

                if (response && response.data.results && response.data.success) {
                    let updatedList = listRef.current.map(x => {
                        if (x.id == id) {
                            return response.data.results;
                        }

                        return x;
                    });

                    setList(updatedList);
                    listRef.current = updatedList;
                } else {
                    setError('Unable to update inventory');
                }
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        putData();
    };

    return { loading, error, list, record, update };
}

export default useStrongBoxOpoPhysicalInventory;
